/**
 * flowArtGeometry.ts — the DRAWING half of a tool's generated preview art.
 *
 * `@trailblazer/shared`'s `flowPreview.ts` answers "what is this flow shaped like?"; this module
 * answers "where do the marks go?". It is deliberately a separate, DOM-free module from `FlowArt.tsx`
 * so the arithmetic can be unit-tested without rendering anything — the two failure modes here are a
 * `NaN` in a path string and a mark drawn outside its box, and both are far easier to assert on a
 * plain array of shapes than on mounted SVG.
 *
 * THREE RULES HOLD ACROSS EVERYTHING HERE:
 *
 * 1. NO COLOUR. A shape names a `paint` — one of eight token-backed classes defined in flowArt.css.
 *    No hex, no `hsl()`, no `rgb()` appears in this file or in `FlowArt.tsx`; the per-tool hue reaches
 *    CSS as a single integer custom property. That is what keeps every colour in the design system,
 *    and what keeps `audit-tokens.mjs` clean without an exemption.
 *
 * 2. NO `<defs>`. No filter, mask, pattern or gradient is ever emitted — see flowArt.css for the
 *    performance reasoning. The practical consequence is that no shape here needs an `id`, so twenty
 *    tiles in a grid cannot collide and `useId()` is unnecessary.
 *
 * 3. EVERYTHING INSIDE THE BOX. Geometry is expressed in REAL PIXELS inside the box it was asked for,
 *    never in a normalised 0..1 square. `FlowArt.tsx` sets `viewBox="0 0 w h"`, whose units ARE CSS
 *    px, so a 2px stroke is 2px at every size and no `aspect-ratio` is needed to keep circles round.
 *    Every position is a fraction of `w` or `h`, so one function serves a 4:3 accordion panel and a
 *    1.53:1 card without distortion.
 */
import {
  buildFlowEmblem,
  FLOW_APP_META,
  makeFlowRng,
  type ConnectorFamily,
  type FlowAppId,
  type FlowComponentKind,
  type FlowEmblemModel,
  type FlowGlyph,
  type FlowPreviewModel,
  type FlowPreviewNode,
} from '@trailblazer/shared';
import { FLUENT_PATHS, hasFluentPath } from './fluentPaths';

/* ------------------------------------------------------------------------------------------------
 * The shape vocabulary
 * ---------------------------------------------------------------------------------------------- */

/**
 * The twelve paints. Each is a class in flowArt.css; the geometry never knows what colour it is.
 *
 * `ground` and `paper` are the SAME COLOUR and are still two paints, because they fill two roles a
 * slot can disagree about. `ground` is the full-box backdrop; `paper` is the knockout in a solid
 * glyph (the ring in `loop`, the hands in `schedule`). The two profile slots arrive with a tint of
 * their own and opt the backdrop out via `.fa--bare`, but a knockout must keep painting there or
 * every solid glyph would gain a hole, and one paint cannot be transparent and opaque at once.
 *
 * `grid` is neutral structure outside the flow. `hair` and `line` are strokes at the two token
 * weights. `fill` and `deep` are the light and dark fills of the per-tool hue. `ink` is body text
 * colour, used only for the overflow numeral and the emblem monogram — the two marks that must stay
 * readable at any hue. `dash` is a dashed hairline, reserved for marks standing for something
 * UNKNOWN (the emblem's uncounted connector ticks), so "we are guessing" has its own register.
 *
 * The three `brand*` paints are the same three roles as `fill`/`deep`/`line` — pale body, dark mark,
 * outline — but composed from `--fa-tone` instead of the per-tool hue. A shape takes one of them ONLY
 * when it also carries a `tone`, which only a recognised app ever sets. That is the whole rule that
 * keeps brand colour meaning "we know exactly which app this is": a family we merely inferred is drawn
 * in the tool's own hue, so a tint is never a guess. flowArt.css mixes each one toward paper or ink
 * rather than using the raw hex, because a brand colour can arrive at any lightness (Power BI's is
 * yellow) and a mark has to stay readable on paper regardless.
 */
export const ART_PAINTS = [
  'ground',
  'paper',
  'grid',
  'hair',
  'line',
  'fill',
  'deep',
  'ink',
  'dash',
  'brand',
  'brand-deep',
  'brand-line',
] as const;
export type ArtPaint = (typeof ART_PAINTS)[number];

interface ArtBase {
  paint: ArtPaint;
  /** Multiplied into the paint's own opacity. Used for the emblem's arc depth ramp. */
  alpha?: number;
  /**
   * The app whose brand colour this shape is painted in. Emitted by FlowArt.tsx as a CLASS
   * (`fa__tone--outlook`), never as a style: the hex itself lives in design-system.css, so no colour
   * literal enters a `.tsx` file and `audit-tokens.mjs` needs no exemption. Typed as `FlowAppId`
   * rather than `string` so the class name can only ever come from the closed twenty-entry table —
   * a connector string is author-controlled and must never reach a class name.
   */
  tone?: FlowAppId;
}

export type ArtShape =
  | (ArtBase & { kind: 'rect'; x: number; y: number; w: number; h: number; rx?: number })
  | (ArtBase & { kind: 'circle'; cx: number; cy: number; r: number })
  | (ArtBase & { kind: 'ellipse'; cx: number; cy: number; rx: number; ry: number })
  | (ArtBase & { kind: 'path'; d: string })
  /**
   * A Fluent icon path, placed by CENTRE and scaled from its 24-unit source box — see `iconMark`.
   * A separate kind from `path` so the 24-unit assumption is declared once here rather than baked
   * into a transform string at each call site, and so a scene test can count icons.
   */
  | (ArtBase & { kind: 'icon'; cx: number; cy: number; size: number; d: string })
  | (ArtBase & {
      kind: 'text';
      x: number;
      y: number;
      text: string;
      size: number;
      anchor: 'start' | 'middle' | 'end';
    });

export interface FlowArtScene {
  shapes: ArtShape[];
}

/* ------------------------------------------------------------------------------------------------
 * Numeric hygiene
 *
 * Every number that reaches a path string goes through `f()`. A single `NaN` silently voids an entire
 * `d` attribute — the element renders as nothing, with no console error and no visual clue beyond a
 * missing mark — so the guard lives here rather than being trusted to callers, and
 * flowArtGeometry.test.ts asserts that no scene at any size contains one.
 * ---------------------------------------------------------------------------------------------- */

interface Pt {
  x: number;
  y: number;
}

/** Round to 2dp for a compact path string, coercing any non-finite value to 0. */
function f(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}

/** A point `len` px from `from` toward `to`. Returns `from` unchanged for a zero-length segment. */
function toward(from: Pt, to: Pt, len: number): Pt {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const d = Math.hypot(dx, dy);
  if (!Number.isFinite(d) || d === 0) return { x: from.x, y: from.y };
  const t = clamp(len / d, 0, 1);
  return { x: from.x + dx * t, y: from.y + dy * t };
}

function moveTo(p: Pt): string {
  return `M${f(p.x)} ${f(p.y)}`;
}

function lineTo(p: Pt): string {
  return `L${f(p.x)} ${f(p.y)}`;
}

/** A straight two-point segment as its own path. */
function segment(a: Pt, b: Pt): string {
  return moveTo(a) + lineTo(b);
}

/**
 * A polyline with its corners cut at 45 degrees — the routing idiom of a circuit board, and the reason
 * the trace reads as a drawn diagram rather than as a bar chart. A corner whose incoming or outgoing
 * segment has zero length is passed through square, since there is no direction to cut.
 */
function chamferedPath(points: readonly Pt[], cut: number): string {
  if (points.length === 0) return '';
  const first = points[0]!;
  if (points.length === 1) return moveTo(first);
  let d = moveTo(first);
  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = points[i - 1]!;
    const cur = points[i]!;
    const next = points[i + 1]!;
    const inLen = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const outLen = Math.hypot(next.x - cur.x, next.y - cur.y);
    if (inLen === 0 || outLen === 0) {
      d += lineTo(cur);
      continue;
    }
    const cutBy = Math.min(cut, inLen / 2, outLen / 2);
    d += lineTo(toward(cur, prev, cutBy)) + lineTo(toward(cur, next, cutBy));
  }
  return d + lineTo(points[points.length - 1]!);
}

/** A point on an ellipse. Angles are radians, 0 = east, growing clockwise on screen. */
function onEllipse(cx: number, cy: number, rx: number, ry: number, angle: number): Pt {
  return { x: cx + Math.cos(angle) * rx, y: cy + Math.sin(angle) * ry };
}

/* ------------------------------------------------------------------------------------------------
 * Flattening
 * ---------------------------------------------------------------------------------------------- */

/** One step in draw order: a flat view of the model, so the scene never re-walks the tree. */
export interface FlatStep {
  id: string;
  depth: number;
  glyph: FlowGlyph;
  family: ConnectorFamily;
  /** The recognised app, or null. Decides whether this step gets a brand mark or a family one. */
  app: FlowAppId | null;
  kind: FlowComponentKind;
  siblingIndex: number;
  siblingCount: number;
  leafCount: number;
  subtreeSize: number;
  branch: string | null;
  /** Index of this step's parent in the flat list, or -1 for a root. */
  parentIndex: number;
}

/**
 * Depth-first flatten, stopping after `limit` steps.
 *
 * The cut is at the END of the sequence rather than pruning deep subtrees, because the flow's opening
 * is what a reader orients on: showing the first N steps and reporting "+M more" is honest, whereas
 * silently dropping the branches at the bottom would present a truncated flow as a complete one.
 *
 * Because the cut is a DFS SUFFIX, no kept step can have a dropped ancestor, so every `parentIndex`
 * that ends up in `steps` is a valid index into `steps`.
 */
export function flattenPreview(
  roots: readonly FlowPreviewNode[],
  limit: number,
): { steps: FlatStep[]; overflow: number } {
  const steps: FlatStep[] = [];
  const cap = Number.isFinite(limit) ? Math.max(0, Math.trunc(limit)) : 0;
  const walk = (node: FlowPreviewNode, parentIndex: number): void => {
    const index = steps.length;
    if (index < cap) {
      steps.push({
        id: node.id,
        depth: node.depth,
        glyph: node.glyph,
        family: node.family,
        app: node.app,
        kind: node.kind,
        siblingIndex: node.siblingIndex,
        siblingCount: node.siblingCount,
        leafCount: node.leafCount,
        subtreeSize: node.subtreeSize,
        branch: node.branch,
        parentIndex,
      });
    }
    // Children of a dropped node are themselves dropped (DFS suffix), so the index handed down past
    // the cap is never read back.
    for (const child of node.children) walk(child, index);
  };
  for (const root of roots) walk(root, -1);
  // The model already computed each root's subtree size, so the total needs no second traversal.
  const total = roots.reduce((sum, r) => sum + r.subtreeSize, 0);
  return { steps, overflow: Math.max(0, total - steps.length) };
}

/**
 * The detail budget, derived from the box rather than passed as a size enum — every call site already
 * knows its pixel dimensions, and a `size="sm"` prop would be a second source of truth for the same
 * fact. The divisor is the one tuning constant: it puts roughly one mark per 22px of the box's
 * geometric mean, which lands at 3 marks in the search dropdown's 64x46, 8 on the wizard's 214x128
 * plate, 14 on a 360x270 accordion panel and 15 in the review card's 415x272.
 *
 * The divisor was 26 while every step drew the same circle, where a denser box only ever meant more
 * of the same mark. Now that a step can draw its own app, more marks means more INFORMATION, so the
 * budget is worth spending — up to the point where a mark stops being legible, which is what the
 * three-rung ladder in `stepMark` handles rather than this number.
 */
export function densityFor(width: number, height: number): number {
  const w = Number.isFinite(width) ? Math.max(1, width) : 1;
  const h = Number.isFinite(height) ? Math.max(1, height) : 1;
  return clamp(Math.round(Math.sqrt(w * h) / 22), 3, 18);
}

/* ------------------------------------------------------------------------------------------------
 * Shared furniture
 * ---------------------------------------------------------------------------------------------- */

/**
 * The full-box backdrop, and the first shape in every scene. It is the ground the GEOMETRY declares,
 * which is what the scene tests pin; `.fa`'s own `background-color` is the ground the LAYOUT needs,
 * covering the letterbox margin that `preserveAspectRatio="meet"` leaves outside the viewBox whenever
 * a box's real ratio differs from the nominal one — a margin no rect in viewBox units can reach.
 *
 * It uses `ground` rather than `paper` — the same colour, deliberately a second paint. A slot that
 * arrives with a tint of its own opts the backdrop out with `.fa--bare`, and that has to be able to
 * make THIS rect transparent without also punching holes through the `paper` knockouts that keep a
 * solid glyph legible. One paint for both roles cannot be transparent and opaque in the same tile.
 */
function ground(w: number, h: number): ArtShape {
  return { kind: 'rect', paint: 'ground', x: 0, y: 0, w, h };
}

/** The marker's type size, and so the extent of the corner it needs. */
function overflowSize(h: number): number {
  return clamp(Math.round(h * 0.1), 8, 14);
}

/**
 * The height of the corner the marker occupies, or 0 when there is nothing to mark.
 *
 * The scene subtracts this from its drawing area so a pad can never land under the numeral. It lives
 * beside the marker rather than in the scene precisely so the two cannot drift apart — a reservation
 * computed from a copy of the marker's own arithmetic is a collision waiting for the next tweak.
 */
function overflowBand(h: number, overflow: number): number {
  return overflow > 0 ? overflowSize(h) * 1.5 : 0;
}

/**
 * The overflow marker: a short tick and a `+N` numeral, bottom-right.
 *
 * It is the honest answer to a flow longer than the detail budget, and it is a NUMERAL rather than a
 * visual hint (a fade, a smaller mark) precisely because "there are 7 steps you cannot see" is a fact,
 * not an impression. Always the same corner, so a reader learns it once.
 */
function overflowMarker(w: number, h: number, overflow: number): ArtShape[] {
  if (overflow <= 0) return [];
  const size = overflowSize(h);
  const y = h - size * 0.45;
  const x = w - size * 0.3;
  const tickX = Math.max(0, x - size * (1.6 + `${overflow}`.length * 0.55));
  return [
    { kind: 'path', paint: 'hair', d: segment({ x: tickX, y: y - size * 0.32 }, { x: tickX, y }) },
    { kind: 'text', paint: 'ink', x, y, text: `+${overflow}`, size, anchor: 'end' },
  ];
}

/**
 * How a glyph is drawn as a mark of radius `r` at `(cx, cy)` — the one place the step vocabulary
 * becomes visible, and the reason the same step type looks the same in a diagram tile and in the
 * wizard's plate. SHAPE, never colour alone, carries the meaning.
 */
function glyphMark(glyph: FlowGlyph, cx: number, cy: number, r: number): ArtShape[] {
  const out: ArtShape[] = [];
  const diamond = (rad: number): string =>
    `${moveTo({ x: cx, y: cy - rad })}${lineTo({ x: cx + rad, y: cy })}${lineTo({
      x: cx,
      y: cy + rad,
    })}${lineTo({ x: cx - rad, y: cy })}Z`;

  switch (glyph) {
    case 'start':
    case 'schedule':
    case 'signal': {
      // A start is a solid disc with a mark inside it, so the flow's beginning is unmistakable.
      out.push({ kind: 'circle', paint: 'deep', cx, cy, r });
      if (glyph === 'schedule') {
        // Clock hands, as two thin paper RECTS rather than a stroked polyline: `paper` is a fill-only
        // paint (see flowArt.css), and a stroked open path in a fill paint would fill the implied
        // closed triangle instead of drawing two hands.
        out.push({
          kind: 'rect',
          paint: 'paper',
          x: cx - r * 0.09,
          y: cy - r * 0.55,
          w: r * 0.18,
          h: r * 0.55,
        });
        out.push({
          kind: 'rect',
          paint: 'paper',
          x: cx - r * 0.09,
          y: cy - r * 0.09,
          w: r * 0.5,
          h: r * 0.18,
        });
      } else if (glyph === 'signal') {
        out.push({ kind: 'circle', paint: 'paper', cx, cy, r: r * 0.38 });
      }
      break;
    }
    case 'branch': {
      // A diamond: two ways out, which is what a condition means.
      out.push({ kind: 'path', paint: 'fill', d: diamond(r) });
      out.push({ kind: 'path', paint: 'line', d: diamond(r) });
      break;
    }
    case 'choose': {
      // A diamond with a bar across it: many ways out, not two.
      out.push({ kind: 'path', paint: 'fill', d: diamond(r) });
      out.push({ kind: 'path', paint: 'line', d: diamond(r) });
      out.push({
        kind: 'path',
        paint: 'line',
        d: segment({ x: cx - r * 0.44, y: cy }, { x: cx + r * 0.44, y: cy }),
      });
      break;
    }
    case 'loop': {
      // A ring: the mark that comes back to itself.
      out.push({ kind: 'circle', paint: 'fill', cx, cy, r });
      out.push({ kind: 'circle', paint: 'line', cx, cy, r });
      out.push({ kind: 'circle', paint: 'paper', cx, cy, r: r * 0.4 });
      break;
    }
    case 'group': {
      out.push({
        kind: 'rect',
        paint: 'grid',
        x: cx - r,
        y: cy - r,
        w: r * 2,
        h: r * 2,
        rx: r * 0.3,
      });
      break;
    }
    case 'compute': {
      // A plain square: shaping data, not moving it.
      out.push({
        kind: 'rect',
        paint: 'fill',
        x: cx - r * 0.74,
        y: cy - r * 0.74,
        w: r * 1.48,
        h: r * 1.48,
      });
      out.push({
        kind: 'rect',
        paint: 'line',
        x: cx - r * 0.74,
        y: cy - r * 0.74,
        w: r * 1.48,
        h: r * 1.48,
      });
      break;
    }
    case 'store': {
      // A slab: something set down and kept.
      out.push({ kind: 'rect', paint: 'deep', x: cx - r, y: cy - r * 0.48, w: r * 2, h: r * 0.96 });
      break;
    }
    case 'pause': {
      out.push({ kind: 'rect', paint: 'deep', x: cx - r * 0.68, y: cy - r, w: r * 0.48, h: r * 2 });
      out.push({ kind: 'rect', paint: 'deep', x: cx + r * 0.2, y: cy - r, w: r * 0.48, h: r * 2 });
      break;
    }
    case 'stop': {
      out.push({
        kind: 'rect',
        paint: 'deep',
        x: cx - r * 0.84,
        y: cy - r * 0.84,
        w: r * 1.68,
        h: r * 1.68,
      });
      break;
    }
    case 'handoff': {
      // A triangle pointing out of the flow: this step hands the work somewhere else.
      out.push({
        kind: 'path',
        paint: 'deep',
        d: `${moveTo({ x: cx - r * 0.78, y: cy - r })}${lineTo({ x: cx + r, y: cy })}${lineTo({
          x: cx - r * 0.78,
          y: cy + r,
        })}Z`,
      });
      break;
    }
    case 'act':
    default: {
      // Reached only through `glyphMark` directly; `stepMark` below intercepts `act` first so it can
      // draw the app or the family. Kept as the last-resort mark for a step with neither.
      out.push({ kind: 'circle', paint: 'fill', cx, cy, r });
      out.push({ kind: 'circle', paint: 'line', cx, cy, r });
      break;
    }
  }
  return out;
}

/* ------------------------------------------------------------------------------------------------
 * The mark ladder: what an action step draws, given how much room it has
 *
 * A control step (branch, loop, stop, ...) always keeps its geometric glyph — those shapes ARE the
 * vocabulary that makes a diagram readable, and no app identity is worth trading them for. It is the
 * plain ACTION step, which was one undifferentiated circle at every size, that this ladder replaces:
 *
 *   r >= ICON_MIN_R    a brand plate carrying the app's Fluent metaphor
 *   r >= LETTER_MIN_R  a brand plate carrying the app's one- or two-letter monogram
 *   below              the family silhouette, tinted with the app's brand colour if we know it
 *
 * The rungs are set by legibility, measured rather than guessed. A Fluent icon is drawn at 1.5x the
 * mark radius and its own hairlines are about a thirteenth of its box, so below ~12.8px across (r 8.5)
 * the thin parts of an envelope or a table start dropping out at 1x DPR. A monogram needs at least 6px
 * of font: at r 5 a two-letter mark is exactly 6px and 7.9px wide inside an 8.5px-wide plate, which is
 * the tightest it can be and still be a letter rather than a smudge.
 *
 * The bottom rung is deliberately the WEAKEST distinction in the system, and that is the honest place
 * for it. Seven family silhouettes cannot all be told apart at 8px across; what survives at that size
 * is the envelope — flat and wide for mail, portrait for a record, tabbed for a folder. A family is
 * also the coarsest thing we know, since it comes from a regex over the connector name rather than a
 * lookup, so the weakest mark carries the weakest claim. Where the exact app IS known, the tint runs
 * on top of the silhouette: shape says family, colour says app, and neither is doing the work alone.
 * ---------------------------------------------------------------------------------------------- */

/** Smallest mark radius that still holds a Fluent icon's hairlines. See the note above. */
const ICON_MIN_R = 8.5;
/** Smallest mark radius that still reads as a letter rather than a smudge. See the note above. */
const LETTER_MIN_R = 5;

/**
 * Repaints a family silhouette in an app's brand colour: `fill`, `line` and `deep` become their
 * `brand*` counterparts and pick up the tone. Every other paint is left alone on purpose — `paper` is
 * a knockout and `hair`/`grid` are neutral structure, and tinting either would punch a coloured hole
 * through a glyph or claim that a divider rule is part of the brand.
 */
function brandify(shapes: readonly ArtShape[], app: FlowAppId): ArtShape[] {
  return shapes.map((shape) => {
    switch (shape.paint) {
      case 'fill':
        return { ...shape, paint: 'brand' as const, tone: app };
      case 'line':
        return { ...shape, paint: 'brand-line' as const, tone: app };
      case 'deep':
        return { ...shape, paint: 'brand-deep' as const, tone: app };
      default:
        return shape;
    }
  });
}

/**
 * A Fluent metaphor, centred on `(cx, cy)` and scaled from its 24-unit source box.
 *
 * Returns nothing at all when the icon name has no bundled path, rather than substituting a different
 * mark: the caller has already decided which rung of the ladder it is on, and a silent swap there
 * would make the rungs untestable. `fluentPaths.ts` and `FLOW_APP_META` are checked against each other
 * by a test instead.
 */
function iconMark(
  icon: string,
  cx: number,
  cy: number,
  r: number,
  paint: ArtPaint,
  app: FlowAppId,
): ArtShape[] {
  const d = FLUENT_PATHS[icon];
  if (d === undefined) return [];
  return [{ kind: 'icon', paint, tone: app, cx, cy, size: r * 1.5, d }];
}

/**
 * The app's monogram, in body `ink` rather than the brand colour.
 *
 * Same reasoning flowArt.css gives for the emblem's monogram: a brand colour arrives at whatever
 * lightness the product happens to use (Power BI's is yellow), so a letter painted in it would vanish
 * against its own pale plate at exactly the size where it is already hardest to read. The plate
 * underneath carries the brand; the letter only has to be legible.
 */
function letterMark(letter: string, cx: number, cy: number, r: number): ArtShape[] {
  // Two caps at a semibold weight run about 1.32em wide, so the size has to come down for them to fit
  // inside the plate; one cap has room to spare. Both land at 6px or more at LETTER_MIN_R.
  const size = letter.length > 1 ? r * 1.2 : r * 1.42;
  return [
    // `.fa__t` sets `dominant-baseline: alphabetic`, so y is the BASELINE. Centring the cap box means
    // lifting the baseline half a cap-height (about 0.36em) below the mark's middle.
    {
      kind: 'text',
      paint: 'ink',
      x: cx,
      y: cy + size * 0.36,
      text: letter,
      size,
      anchor: 'middle',
    },
  ];
}

/**
 * The bottom rung: one silhouette per connector family, in the tool's own hue.
 *
 * Each is two or three shapes and differs from its neighbours by OUTLINE — aspect, corners, a tab —
 * because interior detail is the first thing to go at 8px. `other` is deliberately the exact circle
 * every action step drew before this ladder existed, so a connector we recognise nothing about looks
 * the same as it always did.
 */
function familyMark(family: ConnectorFamily, cx: number, cy: number, r: number): ArtShape[] {
  switch (family) {
    case 'mail': {
      // An envelope: flat and wide, with the fold as its one interior line.
      const x = cx - r;
      const y = cy - r * 0.62;
      const box: ArtShape = {
        kind: 'rect',
        paint: 'fill',
        x,
        y,
        w: r * 2,
        h: r * 1.24,
        rx: r * 0.12,
      };
      return [
        box,
        { ...box, paint: 'line' as const },
        {
          kind: 'path',
          paint: 'line',
          d: `${moveTo({ x, y })}${lineTo({ x: cx, y: cy + r * 0.24 })}${lineTo({
            x: x + r * 2,
            y,
          })}`,
        },
      ];
    }
    case 'chat': {
      // A speech bubble. The tail is the whole distinction, so it is part of the outline rather than a
      // separate mark that a chamfer could swallow.
      const d = chamferedPath(
        [
          { x: cx - r, y: cy - r * 0.85 },
          { x: cx + r, y: cy - r * 0.85 },
          { x: cx + r, y: cy + r * 0.42 },
          { x: cx - r * 0.16, y: cy + r * 0.42 },
          { x: cx - r * 0.62, y: cy + r * 1.1 },
          { x: cx - r * 0.5, y: cy + r * 0.42 },
          { x: cx - r, y: cy + r * 0.42 },
        ],
        r * 0.2,
      );
      return [
        { kind: 'path', paint: 'fill', d },
        { kind: 'path', paint: 'line', d },
      ];
    }
    case 'files': {
      // A folder: the raised tab reads at any size because it breaks the top edge.
      const d = chamferedPath(
        [
          { x: cx - r, y: cy + r * 0.82 },
          { x: cx - r, y: cy - r * 0.82 },
          { x: cx - r * 0.15, y: cy - r * 0.82 },
          { x: cx + r * 0.08, y: cy - r * 0.46 },
          { x: cx + r, y: cy - r * 0.46 },
          { x: cx + r, y: cy + r * 0.82 },
        ],
        r * 0.16,
      );
      return [
        { kind: 'path', paint: 'fill', d },
        { kind: 'path', paint: 'line', d },
      ];
    }
    case 'records': {
      // A record card: portrait, with a header rule. The aspect is what separates it from `mail`.
      const box: ArtShape = {
        kind: 'rect',
        paint: 'fill',
        x: cx - r * 0.78,
        y: cy - r,
        w: r * 1.56,
        h: r * 2,
        rx: r * 0.18,
      };
      return [
        box,
        { ...box, paint: 'line' as const },
        {
          kind: 'path',
          paint: 'hair',
          d: segment(
            { x: cx - r * 0.78, y: cy - r * 0.36 },
            { x: cx + r * 0.78, y: cy - r * 0.36 },
          ),
        },
      ];
    }
    case 'data': {
      // A table: landscape, crossed once each way. Two rules rather than a full grid, so the cells
      // stay open at small sizes instead of silting up.
      const box: ArtShape = {
        kind: 'rect',
        paint: 'fill',
        x: cx - r,
        y: cy - r * 0.78,
        w: r * 2,
        h: r * 1.56,
        rx: r * 0.1,
      };
      return [
        box,
        { ...box, paint: 'line' as const },
        {
          kind: 'path',
          paint: 'hair',
          d: `${segment({ x: cx - r, y: cy }, { x: cx + r, y: cy })}${segment(
            { x: cx, y: cy - r * 0.78 },
            { x: cx, y: cy + r * 0.78 },
          )}`,
        },
      ];
    }
    case 'web': {
      // A globe: a disc with one meridian and one parallel.
      return [
        { kind: 'circle', paint: 'fill', cx, cy, r },
        { kind: 'circle', paint: 'line', cx, cy, r },
        { kind: 'ellipse', paint: 'hair', cx, cy, rx: r * 0.44, ry: r },
        { kind: 'path', paint: 'hair', d: segment({ x: cx - r, y: cy }, { x: cx + r, y: cy }) },
      ];
    }
    case 'other':
    default: {
      return [
        { kind: 'circle', paint: 'fill', cx, cy, r },
        { kind: 'circle', paint: 'line', cx, cy, r },
      ];
    }
  }
}

/**
 * What one step draws. The single entry point every style uses.
 *
 * Two glyphs are intercepted here and the other eleven pass straight through to `glyphMark`:
 *
 * - `act`, the plain action, gets the full ladder. It is the only glyph whose geometric mark said
 *   nothing, so it is the only one with nothing to lose.
 * - `start`, the generic trigger, keeps its solid disc — "the flow begins here" is the first fact a
 *   reader needs — but where there is room the app's metaphor is knocked out of the disc in `paper`
 *   and the disc itself takes the brand colour. `schedule` and `signal` are left alone: their clock
 *   and pip say something the connector does not, and a flow triggered on a timer is more usefully
 *   marked as timed than as Outlook.
 */
function stepMark(step: FlatStep, cx: number, cy: number, r: number): ArtShape[] {
  const meta = step.app ? FLOW_APP_META[step.app] : null;

  if (step.glyph === 'act') {
    if (step.app && meta && r >= LETTER_MIN_R) {
      const plate: ArtShape[] = [
        { kind: 'circle', paint: 'brand', tone: step.app, cx, cy, r },
        { kind: 'circle', paint: 'brand-line', tone: step.app, cx, cy, r },
      ];
      const inner =
        r >= ICON_MIN_R && hasFluentPath(meta.icon)
          ? iconMark(meta.icon, cx, cy, r, 'brand-deep', step.app)
          : letterMark(meta.letter, cx, cy, r);
      return [...plate, ...inner];
    }
    const silhouette = familyMark(step.family, cx, cy, r);
    return step.app ? brandify(silhouette, step.app) : silhouette;
  }

  if (step.glyph === 'start' && step.app && meta && r >= ICON_MIN_R && hasFluentPath(meta.icon)) {
    return [
      { kind: 'circle', paint: 'brand-deep', tone: step.app, cx, cy, r },
      // Knocked OUT of the disc rather than laid on top of it: `paper` is the same fill the ground
      // uses, so the metaphor reads as a hole and the disc stays unmistakably solid.
      ...iconMark(meta.icon, cx, cy, r, 'paper', step.app),
    ];
  }

  return glyphMark(step.glyph, cx, cy, r);
}

/* ------------------------------------------------------------------------------------------------
 * The scene — pads on a serpentine trace
 * ---------------------------------------------------------------------------------------------- */

/** Concentric rings past the third are indistinguishable, so depth is shown up to here and no deeper. */
const MAX_VIA_RINGS = 3;

/**
 * The smallest gap, in real pixels, at which two hairline via rings still read as two rings. Both are
 * `non-scaling-stroke` 1px lines whatever the box does, so this is a screen measurement rather than a
 * proportion — which is exactly why it belongs in the ring budget below rather than in a ratio.
 */
const MIN_VIA_GAP = 2.4;

/** The floor on a mark's radius. Below this a glyph is a dot, but a dot is still honest. */
const MIN_MARK_R = 1.2;

/**
 * How many columns to lay the steps out in.
 *
 * `sqrt(n * innerW / innerH)` — the obvious closed form — gives the column count whose cells come out
 * squarest, and on its own it is wrong often enough to matter: it takes no account of the cells left
 * EMPTY by a partial last row, so seven steps in a wide box land as 3x3 with two cells wasted where
 * 4x2 wastes one and reads tighter. So the closed form is the starting point and the counts around it
 * are scored. `|log(cellW / cellH)|` prefers square cells symmetrically — a 2:1 cell and a 1:2 cell
 * score the same, which a plain ratio would not — and the emptiness term breaks the near-ties.
 *
 * The weight of 1.2 was picked against the real cases: high enough that 4x2 beats 3x3 for seven
 * steps, low enough that three steps in a wide box stay 2x2 instead of collapsing into one tall
 * column, which is what scoring on emptiness alone would have chosen.
 */
function bestColumns(n: number, innerW: number, innerH: number): number {
  const ideal = Math.sqrt((n * innerW) / innerH);
  const from = clamp(Math.floor(ideal) - 1, 1, n);
  const to = clamp(Math.ceil(ideal) + 1, 1, n);
  let best = from;
  let bestScore = Infinity;
  for (let cols = from; cols <= to; cols += 1) {
    const rows = Math.ceil(n / cols);
    const score =
      Math.abs(Math.log(innerW / cols / (innerH / rows))) +
      ((cols * rows - n) / (cols * rows)) * 1.2;
    if (score < bestScore) {
      bestScore = score;
      best = cols;
    }
  }
  return best;
}

/**
 * The scene for a flow whose structure we know: one pad per step, on a serpentine trace.
 *
 * WHY A SERPENTINE rather than a straight line or a tree. A flow IS a sequence, so the drawing has to
 * be one too — but a single row spends the whole width on eight steps at ten pixels each, and at ten
 * pixels a step cannot show which app it uses. Folding the sequence into rows chosen by the box's own
 * shape is what buys a mark big enough to carry its app's icon. Boustrophedon — alternate rows running
 * right-to-left — means the trace never jumps back across the box, so reading order is unambiguous
 * with no arrowheads to draw.
 *
 * EVERY MARK CORRESPONDS TO SOMETHING IN THE FLOW. There is no seeded texture and no filler: pads are
 * steps, rings are nesting depth, the tail is truncation, the numeral is the count that did not fit.
 * An earlier version scattered two or three random off-trace vias for "texture", and they were the one
 * dishonest thing in the drawing — a dot a reader could reasonably take for a step. They are gone, and
 * with them the last use of a random number here: the whole structural tier is now a pure function of
 * the automation. Two tools look different because they ARE different, in length, depth, apps and hue,
 * which is both a stronger distinctiveness than decoration and a promise that a mark means something.
 *
 * A branch or a loop also used to grow a hairline stub off its pad, to say "the flow can go another
 * way" back when a mark was too small to say it itself. The mark now says it — `branch`, `choose` and
 * `loop` each draw their own glyph — and a stub long enough to escape a 15px pad and its vias would
 * have crossed into the next cell. So the stub is gone too and the glyph carries it.
 */
function circuitScene(
  w: number,
  h: number,
  steps: readonly FlatStep[],
  overflow: number,
): FlowArtScene {
  const shapes: ArtShape[] = [ground(w, h)];
  if (steps.length === 0) return { shapes };

  // Tight padding: enough to keep a mark's outline and the trace's turns off the edge, and no more. It
  // was nearly twice this, which spent a third of the height on nothing.
  const padX = w * 0.075;
  const padY = h * 0.1;
  const innerW = Math.max(1, w - padX * 2);
  // The `+N` marker owns the bottom-right corner, so the pads are kept out of its band rather than
  // trusted to miss it. Under the old padding they missed it by arithmetic accident; under this
  // padding a nested step's outer via ring would have run straight through the numeral.
  const innerH = Math.max(1, h - padY * 2 - overflowBand(h, overflow));

  const cols = bestColumns(steps.length, innerW, innerH);
  const rows = Math.max(1, Math.ceil(steps.length / cols));
  const cellW = innerW / cols;
  const cellH = innerH / rows;

  /*
   * Mark size and via spacing are decided TOGETHER, because the depth is information and the mark is
   * only presentation. `usable` is the room from a cell's centre to its edge less a hair, so two
   * neighbours never touch; the rings needed by the deepest step drawn are reserved out of it first,
   * and the mark takes what is left, up to the size it actually wanted.
   *
   * The previous version sized the mark alone and then spaced the rings at half the mark's radius,
   * which put a depth-3 pad's outer ring at 2.5x the mark — wider than the cell, so it overlapped its
   * neighbours at every size, including the one in the screenshot. Reserving first is what makes the
   * vias reliable rather than lucky.
   */
  const halfCell = Math.min(cellW, cellH) * 0.5;
  const usable = halfCell * 0.94;
  const deepest = clamp(Math.max(...steps.map((s) => s.depth)), 0, MAX_VIA_RINGS);
  let rings = deepest;
  const markR = clamp(
    Math.min(Math.min(cellW, cellH) * 0.3, usable - rings * MIN_VIA_GAP),
    MIN_MARK_R,
    // The sparse case needs a ceiling of its own: two steps in a 415x272 plate should read as two
    // nodes, not as two dinner plates.
    Math.max(9, Math.min(w, h) * 0.12),
  );
  // If the mark could not shrink far enough — a tiny box, or a deep flow in a dense one — show fewer
  // layers rather than rings that touch. Dropping the outermost ring understates the nesting; three
  // rings a pixel apart state it in a way nobody can read.
  while (rings > 0 && usable - markR < rings * MIN_VIA_GAP) rings -= 1;
  const ringGap = rings > 0 ? (usable - markR) / rings : 0;

  const centre = (i: number): Pt => {
    const row = Math.floor(i / cols);
    const inRow = i % cols;
    const col = row % 2 === 0 ? inRow : cols - 1 - inRow;
    return { x: padX + (col + 0.5) * cellW, y: padY + (row + 0.5) * cellH };
  };

  // The trace: in from the edge, through every pad, out past each row end and down between the rows.
  const first = centre(0);
  const route: Pt[] = [{ x: Math.max(1, first.x - cellW * 0.5), y: first.y }];
  for (let i = 0; i < steps.length; i += 1) {
    const p = centre(i);
    route.push(p);
    const row = Math.floor(i / cols);
    if (i + 1 < steps.length && Math.floor((i + 1) / cols) !== row) {
      // The turn happens inside the padding, never outside the box.
      const turnX = clamp(p.x + (row % 2 === 0 ? cellW * 0.5 : -cellW * 0.5), 1, w - 1);
      route.push({ x: turnX, y: p.y });
      route.push({ x: turnX, y: centre(i + 1).y });
    }
  }
  // A tail off the board, in the direction the last row was travelling, and ONLY when the flow is
  // longer than the box could show. So a trace that stops at its last pad means "that is the whole
  // flow": the tail is the visual half of the `+N` rather than an ornament.
  if (overflow > 0) {
    const last = centre(steps.length - 1);
    const out = Math.floor((steps.length - 1) / cols) % 2 === 0 ? 1 : -1;
    route.push({ x: clamp(last.x + out * cellW * 0.5, 1, w - 1), y: last.y });
  }
  shapes.push({
    kind: 'path',
    paint: 'line',
    d: chamferedPath(route, Math.min(cellW, cellH) * 0.24),
  });

  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i]!;
    const p = centre(i);
    // Depth as VIAS: a nested step gets concentric rings, so the layer it sits on is visible without
    // moving it off the trace.
    for (let d = 1; d <= Math.min(rings, step.depth); d += 1) {
      shapes.push({ kind: 'circle', paint: 'hair', cx: p.x, cy: p.y, r: markR + d * ringGap });
    }
    shapes.push(...stepMark(step, p.x, p.y, markR));
  }

  shapes.push(...overflowMarker(w, h, overflow));
  return { shapes };
}

/* ------------------------------------------------------------------------------------------------
 * The emblem — for a tool whose structure we do NOT know.
 * ---------------------------------------------------------------------------------------------- */

/**
 * Arcs, a letterform and points. NOTHING ELSE.
 *
 * The grammar is deliberately disjoint from the diagram's: no rails, no boxes, no traces, no pads, no
 * connections, no arrowheads. That separation is the whole point — `api/dataverse/components.ts` sets
 * the rule that an unreadable definition must not look like a simple flow, and a guessed three-box
 * diagram would be a lie a viewer has no way to detect. Someone who has seen one diagram tile and one
 * emblem tile can tell which is which without being told.
 *
 * `pipsAreKnown` splits the pips into two visual registers: FILLED pads when the surface really
 * counted the connectors, DASHED open ticks when the count was seeded. Otherwise decoration would be
 * read as data.
 */
export function emblemScene(
  model: FlowEmblemModel,
  w: number,
  h: number,
  monogramText: string,
): FlowArtScene {
  const shapes: ArtShape[] = [ground(w, h)];
  const rng = makeFlowRng(model.markSeed);
  const cx = w * 0.5;
  const cy = h * 0.52;
  const outerRx = w * 0.36;
  const outerRy = h * 0.36;

  // 2..4 concentric arcs, each a partial ellipse. Never one per step — there is no step count here.
  for (let i = 0; i < model.arcCount; i += 1) {
    const t = 1 - (i * 0.62) / Math.max(1, model.arcCount);
    const rx = outerRx * t;
    const ry = outerRy * t;
    const from = rng() * Math.PI * 2;
    const sweep = Math.PI * (0.75 + rng() * 0.85);
    const a = onEllipse(cx, cy, rx, ry, from);
    const b = onEllipse(cx, cy, rx, ry, from + sweep);
    shapes.push({
      kind: 'path',
      paint: i === 0 ? 'line' : 'hair',
      alpha: clamp(1 - i * 0.2, 0.3, 1),
      d: `${moveTo(a)}A${f(rx)} ${f(ry)} 0 ${sweep > Math.PI ? 1 : 0} 1 ${f(b.x)} ${f(b.y)}`,
    });
  }

  // Connector pips, evenly spaced on the outer arc.
  const pipR = clamp(Math.min(w, h) * 0.035, 1, 4.5);
  for (let i = 0; i < model.pipCount; i += 1) {
    const angle = -Math.PI / 2 + (i / Math.max(1, model.pipCount)) * Math.PI * 2;
    const p = onEllipse(cx, cy, outerRx, outerRy, angle);
    if (model.pipsAreKnown) {
      shapes.push({ kind: 'circle', paint: 'deep', cx: p.x, cy: p.y, r: pipR });
    } else {
      // A DASHED open tick, radially outward — visibly not a counted pad.
      shapes.push({
        kind: 'path',
        paint: 'dash',
        d: segment(p, onEllipse(cx, cy, outerRx * 1.15, outerRy * 1.15, angle)),
      });
    }
  }

  const letters = monogramText.slice(0, 2);
  if (letters) {
    shapes.push({
      kind: 'text',
      paint: 'ink',
      x: cx,
      y: cy + Math.min(w, h) * 0.12,
      text: letters,
      size: clamp(Math.min(w, h) * 0.34, 9, 72),
      anchor: 'middle',
    });
  }
  return { shapes };
}

/* ------------------------------------------------------------------------------------------------
 * Entry points
 * ---------------------------------------------------------------------------------------------- */

/** The smallest box the scene is asked to fill. Below this the arithmetic is meaningless, not wrong. */
const MIN_BOX = 8;

function safeBox(width: number, height: number): { w: number; h: number } {
  return {
    w: Number.isFinite(width) ? Math.max(MIN_BOX, width) : MIN_BOX,
    h: Number.isFinite(height) ? Math.max(MIN_BOX, height) : MIN_BOX,
  };
}

/**
 * Build the scene for a flow whose structure we know.
 *
 * A pure function of the flow and the box, with no seed and no nonce: the same tool draws the same
 * picture for every viewer, on every device, on every reload. That is a property worth naming, because
 * the tile is presented as a description of the automation — if it could vary, it could not be one.
 */
export function buildStructuralScene(
  model: FlowPreviewModel,
  width: number,
  height: number,
): FlowArtScene {
  const { w, h } = safeBox(width, height);
  const { steps } = flattenPreview(model.roots, densityFor(w, h));
  // `stats.stepCount` is the UNTRUNCATED total, so it also accounts for anything `buildFlowPreview`
  // itself dropped at MAX_PREVIEW_NODES — the honest number to put next to the "+".
  const overflow = Math.max(0, model.stats.stepCount - steps.length);
  return circuitScene(w, h, steps, overflow);
}

/** Build the emblem scene. Kept beside `buildStructuralScene` so the two tiers are one decision. */
export function buildEmblemScene(
  seed: number,
  monogramText: string,
  width: number,
  height: number,
  known?: { connectorCount?: number | null; categoryLabel?: string | null; hasFlow?: boolean },
): FlowArtScene {
  const { w, h } = safeBox(width, height);
  return emblemScene(buildFlowEmblem({ ...known, seed }), w, h, monogramText);
}
